import { Controller } from "@hotwired/stimulus"

interface BarDatum {
  name: string
  slug: string
  url: string | null
  elo: number
  win_rate: number | null
  battles: number
  creator_org: string
  creator_slug: string
  brand_icon_url: string | null
}

type Mode = "elo" | "winrate"

const COLUMN_HEIGHT_DESKTOP = 240
const COLUMN_HEIGHT_MOBILE = 180
const MIN_BAR_HEIGHT = 6
const VALUE_INSIDE_THRESHOLD = 36 // px — below this, render value above the bar

export default class extends Controller {
  static targets = ["bar", "shape", "value", "modeButton", "modeLabel", "filterLabel", "subtitle", "plot", "bars"]
  static values = {
    data: Array,
    mode: { type: String, default: "elo" },
    filter: { type: String, default: "all" },
  }

  declare barTargets: HTMLElement[]
  declare shapeTargets: HTMLElement[]
  declare valueTargets: HTMLElement[]
  declare modeButtonTargets: HTMLButtonElement[]
  declare modeLabelTarget: HTMLElement
  declare hasModeLabelTarget: boolean
  declare filterLabelTarget: HTMLElement
  declare hasFilterLabelTarget: boolean
  declare subtitleTarget: HTMLElement
  declare hasSubtitleTarget: boolean
  declare plotTarget: HTMLElement
  declare hasPlotTarget: boolean
  declare barsTarget: HTMLElement
  declare hasBarsTarget: boolean

  declare dataValue: BarDatum[]
  declare modeValue: Mode
  declare filterValue: string

  private resizeObserver?: ResizeObserver
  private rafId: number | null = null

  connect() {
    this.layout()
    // Re-layout on viewport / container changes so the bar heights stay scaled correctly.
    // rAF-throttle the observer so bursts of resize notifications collapse into one layout.
    this.resizeObserver = new ResizeObserver(() => {
      if (this.rafId !== null) return
      this.rafId = requestAnimationFrame(() => {
        this.rafId = null
        this.layout()
      })
    })
    if (this.hasBarsTarget) this.resizeObserver.observe(this.barsTarget)
  }

  disconnect() {
    this.resizeObserver?.disconnect()
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId)
      this.rafId = null
    }
  }

  setMode(event: Event) {
    event.preventDefault()
    const target = event.currentTarget as HTMLElement
    const mode = (target.dataset.mode as Mode) || "elo"
    if (mode === this.modeValue) return
    this.modeValue = mode

    for (const btn of this.modeButtonTargets) {
      const pressed = btn.dataset.mode === mode
      btn.setAttribute("aria-pressed", String(pressed))
    }

    if (this.hasModeLabelTarget) {
      this.modeLabelTarget.textContent = mode === "elo" ? "Elo rating" : "Win rate"
    }

    this.layout()
  }

  setFilter(event: Event) {
    event.preventDefault()
    const target = event.currentTarget as HTMLElement
    const slug = target.dataset.creatorSlug || "all"
    this.filterValue = slug

    if (this.hasFilterLabelTarget) {
      const label = target.textContent?.trim() || "All Models"
      this.filterLabelTarget.textContent = label
    }

    // Close any open daisyUI dropdown by blurring the focused trigger
    const active = document.activeElement as HTMLElement | null
    if (active && typeof active.blur === "function") active.blur()

    this.layout()
  }

  private metricFor(bar: HTMLElement): number | null {
    if (this.modeValue === "elo") {
      const v = parseFloat(bar.dataset.elo || "")
      return Number.isFinite(v) ? v : null
    }
    const v = parseFloat(bar.dataset.winRate || "")
    return Number.isFinite(v) && v >= 0 ? v : null
  }

  private columnHeight(): number {
    return window.matchMedia("(max-width: 640px)").matches ? COLUMN_HEIGHT_MOBILE : COLUMN_HEIGHT_DESKTOP
  }

  private layout() {
    const visibleBars: HTMLElement[] = []

    // Filter pass — show/hide bars based on creator
    for (const bar of this.barTargets) {
      const matches = this.filterValue === "all" || bar.dataset.creator === this.filterValue
      if (matches) {
        bar.removeAttribute("data-hidden")
        visibleBars.push(bar)
      } else {
        bar.setAttribute("data-hidden", "true")
      }
    }

    if (visibleBars.length === 0) return

    // Compute metric values for visible bars; bars with no metric available stay at min height
    const metrics = visibleBars.map(b => this.metricFor(b))
    const validValues = metrics.filter((v): v is number => v != null && v > 0)
    if (validValues.length === 0) return

    // For Elo: scale so the leader fills the column and the smallest is at ~25% (preserves rhythm).
    // For win rate: anchor at 0–100% so the bars read like percentages.
    let toHeight: (v: number | null) => number
    const columnHeight = this.columnHeight()

    if (this.modeValue === "winrate") {
      const max = Math.max(100, ...validValues)
      toHeight = (v) => {
        if (v == null) return MIN_BAR_HEIGHT
        return Math.max(MIN_BAR_HEIGHT, (v / max) * columnHeight)
      }
    } else {
      // Range-based normalization: tallest bar fills the column, shortest bar gets
      // a visible floor (~22% of column height) so the chart reads as ranked, not flat.
      // Using (value - scaledMin) / range avoids the bug where Elo values clustered in a
      // narrow high band (e.g. 1380–1620) all map to ~maxValue / maxValue ≈ 1.0.
      const max = Math.max(...validValues)
      const min = Math.min(...validValues)
      const MIN_VISIBLE_FRACTION = 0.22
      const minPx = columnHeight * MIN_VISIBLE_FRACTION
      // Pull the effective floor below the actual min so the lowest bar is still visible
      const rangeSpan = max - min
      // If all bars have the same value, give them all full height
      const effectiveRange = Math.max(rangeSpan, 1)
      // scaledMin anchors the bottom of the chart so the lowest bar sits at minPx
      // and the highest bar sits at columnHeight
      toHeight = (v) => {
        if (v == null) return MIN_BAR_HEIGHT
        // Linearly interpolate: min → minPx, max → columnHeight
        const fraction = (v - min) / effectiveRange
        const h = minPx + fraction * (columnHeight - minPx)
        return Math.max(MIN_BAR_HEIGHT, Math.min(columnHeight, h))
      }
    }

    // Apply heights + value labels
    visibleBars.forEach((bar, idx) => {
      const metric = metrics[idx]
      const shape = bar.querySelector<HTMLElement>("[data-bar-chart-target='shape']")
      const value = bar.querySelector<HTMLElement>("[data-bar-chart-target='value']")
      if (!shape || !value) return

      const h = toHeight(metric)
      shape.style.height = `${h}px`

      // Update value label
      if (metric == null) {
        value.textContent = "—"
      } else if (this.modeValue === "winrate") {
        value.textContent = `${Math.round(metric)}%`
      } else {
        value.textContent = String(Math.round(metric))
      }

      // Move value above the bar if it's too short to contain the label legibly
      if (h < VALUE_INSIDE_THRESHOLD) {
        value.dataset.position = "outside"
      } else {
        value.dataset.position = "inside"
      }
    })

    // Rebuild the subtitle line. The mode label lives inside the subtitle, so
    // we reconstruct the whole string (mode word included) rather than blowing
    // away the inline modeLabel span with a flat textContent assignment.
    if (this.hasSubtitleTarget) {
      const modeWord = this.modeValue === "elo" ? "Elo rating" : "win rate"
      let text: string
      if (this.filterValue === "all") {
        text = `Top ${visibleBars.length} of ${this.barTargets.length} models · ranked by ${modeWord} · community-voted`
      } else {
        const creatorLabel = this.hasFilterLabelTarget ? this.filterLabelTarget.textContent?.trim() : null
        text = `${visibleBars.length} ${creatorLabel || "filtered"} models in top ${this.barTargets.length} · ranked by ${modeWord}`
      }
      this.subtitleTarget.textContent = text
    }
  }
}
